import { ComponentProps, PropsWithChildren, useMemo } from 'react';
import { Platform, StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { CreateResponsiveStyle, DEVICE_SIZES, minSize } from 'rn-responsive-styles';
import { useBoolean } from 'usehooks-ts';

import { Divider, KeyboardAvoidingView, Text, CloseIcon, spacing, colors, Link } from '@pletly/ui';
import { MAX_COMMENT_LENGTH } from '@pletly/shared';
import { ListDefaultLayout } from '%components';
import { PostComment } from '%components/Posts/Post/Comments';
import { useKeyboardWillShow } from '%hooks/useKeyboardWillShow';
import { useCommentActions } from '%components/PostComments/useCommentActions';
import { CommentInput } from './CommentInput';

interface Props extends PropsWithChildren {
	postId: string;
	comments: ComponentProps<typeof PostComment>['comment'][];
	maxComments?: number;
	commentStyle?: StyleProp<ViewStyle>;
}

export function PostCommentsList({ postId, children, comments, maxComments, commentStyle }: Props) {
	const { t } = useTranslation();
	const styles = useStyles();
	const keyboardWillShow = useKeyboardWillShow();
	const {
		commentInput,
		showCommentActions,
		isEditMode,
		cancelCommentEdit,
		submitComment,
		handleEditCommentClick,
		handleDeleteCommentClick,
		blockedCommenting,
		selectedComment,
	} = useCommentActions(postId);
	const { value: isMoreCommentsDisplayed, toggle } = useBoolean(false);
	const displayedComments =
		isMoreCommentsDisplayed || !maxComments ? comments : comments.slice(0, maxComments);

	const inputBottomPadding = useMemo(() => {
		if (Platform.OS === 'ios') {
			return keyboardWillShow ? 16 : 0;
		}

		return 16;
	}, [keyboardWillShow]);

	const canSendComment = commentInput.value.length > 0;

	return (
		<KeyboardAvoidingView>
			<ListDefaultLayout
				ListHeaderComponent={
					<>
						{children}
						<Divider style={styles.divider} />
					</>
				}
				ListFooterComponent={
					maxComments && comments.length > maxComments ? (
						<Link to="" onPress={toggle} style={styles.moreComments}>
							<Text>{isMoreCommentsDisplayed ? 'Less comments' : 'Show more comments'}</Text>
						</Link>
					) : undefined
				}
				data={displayedComments}
				extraData={selectedComment}
				renderItem={({ item: comment }) => (
					<PostComment
						style={commentStyle}
						comment={comment}
						onEditComment={handleEditCommentClick}
						onDeleteComment={handleDeleteCommentClick}
						onOptionsPress={showCommentActions(comment)}
						canChangeComment={!blockedCommenting}
					/>
				)}
				keyExtractor={keyExtractor}
				estimatedItemSize={96}
				style={[styles.root, !children && styles.transparent]}
			/>
			{!blockedCommenting && (
				<SafeAreaView
					edges={['bottom']}
					style={[
						styles.inputsContainer,
						styles.root,
						{
							paddingBottom: inputBottomPadding,
						},
						!!children && styles.bottomGap,
						!children && styles.transparent,
					]}
				>
					{isEditMode && (
						<View style={styles.editCommentInfo}>
							<Text>{t('post.editing-comment')}</Text>
							<TouchableOpacity onPress={cancelCommentEdit}>
								<CloseIcon viewBox="0 0 32 32" width={16} height={16} />
							</TouchableOpacity>
						</View>
					)}
					<CommentInput
						{...commentInput}
						placeholder={t('post.add-a-comment')}
						onSubmit={submitComment}
						submitDisabled={!canSendComment}
						maxLength={MAX_COMMENT_LENGTH}
					/>
				</SafeAreaView>
			)}
		</KeyboardAvoidingView>
	);
}

const keyExtractor = <Item extends { id: string }>(item: Item): any => item.id;

const MAX_WIDTH_MD = 736;
const MAX_WIDTH_LG = 790;
const MAX_WIDTH_XL = 843;

const useStyles = CreateResponsiveStyle(
	{
		root: {
			...Platform.select({
				web: {
					paddingHorizontal: spacing(3),
					backgroundColor: colors.white,
					borderRadius: 12,
				},
				default: {},
			}),
		},
		transparent: {
			...Platform.select({
				web: {
					backgroundColor: colors.transparent,
				},
				default: {},
			}),
		},
		inputsContainer: {
			backgroundColor: Platform.select({ native: colors.white, default: undefined }),
			paddingHorizontal: spacing(2),
			paddingTop: spacing(2),
			...Platform.select({
				web: {
					marginTop: -spacing(),
					borderTopLeftRadius: 0,
					borderTopRightRadius: 0,
				},
				default: {},
			}),
		},
		bottomGap: {
			...Platform.select({
				web: {
					marginBottom: spacing(4),
				},
				default: {},
			}),
		},
		divider: {
			alignSelf: 'stretch',
			backgroundColor: colors.background300,
			marginVertical: spacing(),
		},
		editCommentInfo: {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			paddingHorizontal: spacing(2),
			paddingVertical: spacing(0.5),
			backgroundColor: colors.gray,
		},
		moreComments: { width: 'fit-content' },
	},
	{
		[minSize(DEVICE_SIZES.MD)]: {
			root: {
				maxWidth: MAX_WIDTH_MD,
			},
			inputsContainer: {
				maxWidth: MAX_WIDTH_MD,
				width: '100%',
				alignSelf: 'center',
			},
		},
		[minSize(DEVICE_SIZES.LG)]: {
			root: {
				maxWidth: MAX_WIDTH_LG,
			},
			inputsContainer: {
				maxWidth: MAX_WIDTH_LG,
			},
		},
		[minSize(DEVICE_SIZES.XL)]: {
			root: {
				maxWidth: MAX_WIDTH_XL,
			},
			inputsContainer: {
				maxWidth: MAX_WIDTH_XL,
			},
		},
	},
);
